import React, { useEffect, useState } from 'react';
import { getHLSStatus, HLSStatus } from './musicPlayerApi';
import './HLSLoadingBanner.css';

interface HLSLoadingBannerProps {
  onStatusChange?: (isComplete: boolean) => void;
}

const HLSLoadingBanner: React.FC<HLSLoadingBannerProps> = ({ onStatusChange }) => {
  const [status, setStatus] = useState<HLSStatus | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkStatus = async () => {
      const hlsStatus = await getHLSStatus();
      if (hlsStatus) {
        setStatus(hlsStatus);

        // Show overlay if generation is in progress or not complete
        const shouldShow = hlsStatus.isGenerating || !hlsStatus.isComplete;
        setIsVisible(shouldShow);
        setIsExiting(false);

        // Notify parent of status change
        if (onStatusChange) {
          onStatusChange(hlsStatus.isComplete);
        }

        // If complete, start exit animation
        if (hlsStatus.isComplete && !hlsStatus.isGenerating) {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => {
              setIsVisible(false);
            }, 600); // Match animation duration
          }, 2000);
        }
      }
    };

    // Check immediately
    checkStatus();

    // Poll every 2 seconds while generating
    intervalId = setInterval(checkStatus, 2000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [onStatusChange]);

  if (!isVisible || !status) {
    return null;
  }

  const { isGenerating, progress, completed, total, isComplete } = status;

  return (
    <div className={`hls-overlay ${isExiting ? 'hls-overlay-exit' : 'hls-overlay-enter'}`}>
      {/* Background blur effect */}
      <div className="hls-overlay-bg"></div>

      {/* Content */}
      <div className="hls-overlay-content">
        {/* Animated music notes background */}
        <div className="hls-notes-bg">
          <div className="hls-note hls-note-1">♪</div>
          <div className="hls-note hls-note-2">♫</div>
          <div className="hls-note hls-note-3">♪</div>
          <div className="hls-note hls-note-4">♫</div>
          <div className="hls-note hls-note-5">♪</div>
        </div>

        {/* Main content container */}
        <div className="hls-content-container">
          {/* Spinner or checkmark */}
          <div className="hls-icon-container">
            {isGenerating || !isComplete ? (
              <div className="hls-spinner">
                <div className="hls-spinner-ring"></div>
                <div className="hls-spinner-ring"></div>
                <div className="hls-spinner-ring"></div>
              </div>
            ) : (
              <div className="hls-checkmark">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            )}
          </div>

          {/* Text content */}
          <div className="hls-text-container">
            <h1 className="hls-title">
              {isGenerating ? 'Preparing Music Library' : isComplete ? 'Ready to Play!' : 'Loading Library'}
            </h1>
            <p className="hls-subtitle">
              {isGenerating
                ? `Generating HLS streams... ${completed}/${total} songs ready`
                : isComplete
                ? `All ${total} songs loaded and ready to play!`
                : `Loading... ${completed}/${total} songs ready`
              }
            </p>
          </div>

          {/* Progress bar */}
          <div className="hls-progress-container">
            <div className="hls-progress-bar">
              <div
                className="hls-progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="hls-progress-text">
              <span className="hls-progress-number">{progress}%</span>
              <span className="hls-progress-label">{completed}/{total}</span>
            </div>
          </div>

          {/* Animated dots */}
          {(isGenerating || !isComplete) && (
            <div className="hls-dots">
              <span className="hls-dot"></span>
              <span className="hls-dot"></span>
              <span className="hls-dot"></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HLSLoadingBanner;

